import React, { useEffect, useState } from 'react';
import { Unlink, X, CornerUpLeft, AlertTriangle, Info, Ban } from 'lucide-react';
import { Button } from '../Button';
import { loadProjectById } from '../../services/cloudService';
import { linkReturnPlanFingerprint, planLinkReturn, type LinkReturnPlan } from '../../services/projectLv';
import { getServiceLog } from '../../services/serviceJournal';
import { ServiceItem } from '../../types';

/**
 * Der Plan VOR dem Trennen — das Gegenstueck zu ProjectLinkingModal.
 *
 * Trennen war bis hierher ein `window.confirm`-Einzeiler, waehrend Verknuepfen
 * ein angezeigter Umbau ist. Das war nicht nur unschoen: die Positionen, die
 * beim Verknuepfen an die Mutter gewandert sind, blieben dort liegen, und
 * niemand erfuhr davon. Wer trennt, muss vorher sehen, was zurueckgeht, was
 * bleibt und warum.
 *
 * Derselbe Aufbau wie beim Verknuepfen, aus denselben Gruenden:
 *   • Der Plan steht VOR dem Ausfuehren.
 *   • Und er wird beim Bestaetigen aus FRISCHEM Stand neu gerechnet: weicht er
 *     vom angezeigten ab, wird nicht ausgefuehrt, sondern neu gezeigt.
 *   • Ein Fehler bleibt im Dialog stehen (kein `alert`), damit der Vorgang
 *     wiederholbar ist, ohne die Auskunft zu verlieren.
 *   • Preise nur mit `canSeeFinancials` (admin | super_admin). Vorgabe FALSE —
 *     eine vergessene Aufrufstelle verschweigt den Preis.
 */
interface Props {
    isOpen: boolean;
    onClose: () => void;
    /** Die Tochter — ihre id steht als Herkunft an den gewanderten Positionen. */
    childProjectId: string;
    childProjectName?: string;
    parentProjectId: string;
    parentProjectName?: string;
    canSeeFinancials?: boolean;
    /** Fuehrt aus. Wirft, wenn das Zurueckholen scheitert — dann wird NICHT getrennt. */
    onConfirm: (plan: LinkReturnPlan) => Promise<void> | void;
}

const formatAmount = (value: number): string => (value || 0).toLocaleString('de-DE');

const formatPrice = (value: number): string =>
    (value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/** "2.7.50 DGUV-Begehung" — ohne LV-Nummer nur der Titel. */
const serviceLabel = (service: ServiceItem): string =>
    [service.lvPosition, service.title].filter(Boolean).join(' ') || 'Position ohne Bezeichnung';

/** Der Text, wenn das Mutterprojekt nicht zu laden ist — an beiden Stellen derselbe. */
const LADEFEHLER = 'Das Mutterprojekt ließ sich nicht laden. Solange nicht feststeht, welche '
    + 'Leistungen dort von diesem Projekt stammen, wird nicht getrennt.';

/**
 * Laedt das Mutterprojekt und rechnet den Plan daraus — `null`, wenn es sich
 * nicht laden laesst.
 *
 * EINE Stelle fuer beides: das Anzeigen beim Oeffnen und die Gegenprobe beim
 * Bestaetigen muessen dieselbe Rechnung anstellen. Zwei Fassungen davon
 * verglichen frueher oder spaeter zwei verschiedene Dinge und meldeten eine
 * Aenderung, die keine ist (oder keine, wo eine ist).
 *
 * Bewusst ausserhalb der Komponente: der Effekt haette die Funktion sonst in
 * seiner Abhaengigkeitsliste, und eine bei jedem Rendern neu entstehende
 * Funktion liesse ihn bei jedem Rendern erneut laufen.
 */
const planAusFrischemStand = async (
    parentProjectId: string,
    childProjectId: string,
): Promise<LinkReturnPlan | null> => {
    const parent = await loadProjectById(parentProjectId, undefined, { skipMigration: true });
    if (!parent || !parent.data) return null;
    return planLinkReturn(
        parent.data.metadata?.globalData?.services,
        childProjectId,
        getServiceLog(parent.data),
    );
};

export const ProjectUnlinkModal: React.FC<Props> = ({
    isOpen,
    onClose,
    childProjectId,
    childProjectName,
    parentProjectId,
    parentProjectName,
    canSeeFinancials = false,
    onConfirm,
}) => {
    const [plan, setPlan] = useState<LinkReturnPlan | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    /**
     * Der Plan entsteht aus dem FRISCH geladenen Mutterprojekt und nicht aus
     * dem Anzeigestand der Hub-Ansicht: zwischen dem Oeffnen des Projekts und
     * dem Trennen kann an der Mutter gebucht oder abgerechnet worden sein, und
     * genau davon haengt ab, was zurueckgehen darf.
     *
     * Scheitert das Laden, wird NICHT getrennt (`loadError` sperrt): ohne die
     * Positionen der Mutter waere das Trennen wieder der alte Schalter, der
     * die Leistungen dort liegen laesst.
     */
    useEffect(() => {
        if (!isOpen) return;
        let abgebrochen = false;
        setPlan(null);
        setLoadError(null);
        setError(null);
        planAusFrischemStand(parentProjectId, childProjectId)
            .then(frisch => {
                if (abgebrochen) return;
                if (!frisch) { setLoadError(LADEFEHLER); return; }
                setPlan(frisch);
            })
            .catch(() => {
                if (!abgebrochen) setLoadError(LADEFEHLER);
            });
        // Aufgeraeumt wird ueber das Flag: eine spaet eintreffende Antwort auf
        // einen geschlossenen Dialog darf den Zustand nicht mehr anfassen.
        return () => { abgebrochen = true; };
    }, [isOpen, parentProjectId, childProjectId]);

    const handleConfirm = async () => {
        if (!plan || busy || loadError) return;
        setError(null);
        setBusy(true);
        try {
            // ─── DER PLAN WIRD UNMITTELBAR VOR DEM AUSFUEHREN NEU GERECHNET ──
            // Der angezeigte Plan stammt aus dem Stand, der beim OEFFNEN des
            // Dialogs galt. Steht der Dialog eine Weile offen, kann an der
            // Mutter inzwischen eine der zurueckgehenden Positionen ABGERECHNET
            // worden sein — dann muss sie dortbleiben (`billed_at_parent`).
            // Wanderte sie trotzdem mit, fande `assertServicesExist` ihre
            // `serviceId` im Beleg der Mutter nicht mehr, und der Beleg liesse
            // sich weder stornieren noch gutschreiben (Vorfall RE-26081024).
            // Dieselbe Gegenprobe wie beim Verknuepfen, aus demselben Grund.
            const frischerPlan = await planAusFrischemStand(parentProjectId, childProjectId);
            if (!frischerPlan) { setError(LADEFEHLER); return; }
            if (linkReturnPlanFingerprint(frischerPlan) !== linkReturnPlanFingerprint(plan)) {
                // Der neue Stand gehoert VOR die Augen des Anwenders, nicht
                // still in die Ausfuehrung — bestaetigt hat er einen anderen
                // Plan. Es wird nichts getrennt und nichts zurueckgeholt.
                setPlan(frischerPlan);
                setError('Der Bestand des Mutterprojekts hat sich seit der Anzeige geändert. Der Plan wurde '
                    + 'neu berechnet — bitte prüfen und erneut bestätigen. Es wurde nichts zurückgeholt und '
                    + 'nicht getrennt.');
                return;
            }
            // Erst zurueckholen, dann schliessen: schlaegt das Umhaengen fehl,
            // bleibt der Dialog mit der Meldung stehen und die Verknuepfung
            // besteht weiter. Eine Tochter ohne LV waere das schlechtere
            // Ergebnis als eine Verknuepfung, die noch steht.
            //
            // Ausgefuehrt wird der FRISCHE Plan: auf ihm ist die Gegenprobe
            // gerechnet, und seine Objekte sind der Stand, der jetzt in der
            // Datenbank steht.
            await onConfirm(frischerPlan);
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Die Leistungen konnten nicht zurückgeholt werden. Es wurde nicht getrennt.');
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
                <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Unlink className="w-5 h-5 text-blue-400" />
                        Verknüpfung aufheben
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/10 p-1 rounded"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto">
                    <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-100">
                        <p>
                            <strong>{childProjectName || 'Dieses Projekt'}</strong> ist nach dem Trennen wieder ein
                            eigenständiges Projekt{parentProjectName ? <> und rechnet nicht mehr über <strong>{parentProjectName}</strong> ab</> : null}.
                        </p>
                        <p className="mt-1 text-xs opacity-80">
                            Die Leistungen, die beim Verknüpfen an das Mutterprojekt gewandert sind, kommen dabei
                            zurück — samt ihren Protokolleinträgen.
                        </p>
                    </div>

                    {loadError && (
                        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm flex gap-2">
                            <Ban className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold">Trennen nicht möglich</p>
                                <p className="mt-1 text-[12px] leading-relaxed">{loadError}</p>
                            </div>
                        </div>
                    )}

                    {!plan && !loadError && (
                        <p className="text-sm text-slate-500">Bestand des Mutterprojekts wird geprüft…</p>
                    )}

                    {plan && plan.moves.length > 0 && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                            <p className="text-sm font-bold text-emerald-900 flex items-center gap-2">
                                <CornerUpLeft className="w-4 h-4 shrink-0" />
                                {plan.moves.length === 1
                                    ? 'Diese Leistung kommt an dieses Projekt zurück'
                                    : `Diese ${plan.moves.length} Leistungen kommen an dieses Projekt zurück`}
                            </p>
                            <ul className="mt-3 space-y-2">
                                {plan.moves.map(move => (
                                    <li key={move.service.id} className="text-xs text-emerald-900 bg-white/70 rounded-lg p-2 border border-emerald-100">
                                        <span className="font-bold">{serviceLabel(move.service)}</span>
                                        <span className="block opacity-80 mt-0.5">
                                            {formatAmount(move.service.quantityUsed)} von{' '}
                                            {formatAmount(move.service.quantityContracted)} {move.service.unit} gebucht
                                            {canSeeFinancials ? <> · {formatPrice(move.service.unitPrice)} je {move.service.unit}</> : null}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {plan && plan.moves.length === 0 && (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
                            <p className="font-bold">Es kommt keine Leistung zurück.</p>
                            <p className="mt-1 text-[12px] leading-relaxed">
                                Im Mutterprojekt ist keine Position vermerkt, die aus diesem Projekt stammt und
                                zurückgehen darf. Was das bedeutet, steht unten.
                            </p>
                        </div>
                    )}

                    {plan && (plan.holds.length > 0 || plan.notes.length > 0) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                Das bleibt beim Mutterprojekt
                            </p>

                            <ul className="mt-3 space-y-2">
                                {plan.holds.map(hold => (
                                    <li key={hold.service.id} className="text-xs text-amber-900 bg-white/70 rounded-lg p-2 border border-amber-100">
                                        <span className="font-bold">{serviceLabel(hold.service)}</span>
                                        <span className="block opacity-80 mt-0.5">
                                            {hold.reason === 'billed_at_parent' && (
                                                <>wurde seit dem Umzug im Mutterprojekt <strong>abgerechnet</strong>
                                                {hold.bookingReferences?.length
                                                    ? <> ({hold.bookingReferences.join(', ')})</>
                                                    : null}. Die Rechnung zeigt auf diese Zeile: nähme man sie mit,
                                                liesse sich der Beleg weder stornieren noch gutschreiben, und die
                                                nächste kumulative Rechnung des Mutterprojekts rechnete gegen sich
                                                selbst. Sie bleibt dort und ist von Hand zu klären.</>
                                            )}
                                            {hold.reason === 'flat_rate' && (
                                                <>trägt eine <strong>Monatspauschale</strong>. Die Positionen, die
                                                sie abgilt, stehen im Mutterprojekt — mitgenommen wären sie dort
                                                wieder offenes Geld und würden ein zweites Mal gefordert.</>
                                            )}
                                            {hold.reason === 'flat_covered' && (
                                                <>ist im Mutterprojekt von einer <strong>Monatspauschale gedeckt</strong>.
                                                Ihre offene Menge ist damit bezahlt; hier wäre sie wieder offenes Geld.</>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            {plan.notes.includes('unstamped_parent_services') && (
                                <p className="mt-3 text-[11px] text-amber-900/90 flex gap-2">
                                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>
                                        <strong>{formatAmount(plan.unstamped)}</strong> Position(en) des
                                        Mutterprojekts tragen keinen Herkunftsvermerk. Das sind die eigenen
                                        Positionen der Mutter — und Umzüge aus der Zeit vor dem Vermerk, die keine
                                        Spur hinterlassen haben. Ob darunter noch etwas aus diesem Projekt liegt,
                                        lässt sich nicht feststellen; geraten wird nichts.
                                    </span>
                                </p>
                            )}
                            {plan.notes.includes('merged_untraceable') && (
                                <p className="mt-2 text-[11px] text-amber-900/90 flex gap-2">
                                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>
                                        Mengen, die beim Verknüpfen mit einer gleichnamigen Position des
                                        Mutterprojekts <strong>zusammengeführt</strong> wurden, bleiben dort: welcher
                                        Anteil der gemeinsamen Menge einmal von hier kam, ist nirgends festgehalten.
                                        Umgekehrt kann eine zurückkommende Position die Menge eines anderen
                                        Tochterprojekts enthalten — auch das ist nicht mehr zu trennen. Bitte die
                                        beauftragten Mengen nach dem Trennen prüfen.
                                    </span>
                                </p>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm flex gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
                    <Button variant="primary" onClick={handleConfirm} disabled={!plan || busy || !!loadError}>
                        {busy
                            ? 'Wird zurückgeholt…'
                            : plan && plan.moves.length > 0 ? 'Zurückholen & trennen' : 'Trennen'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
